import React, { useState, useEffect } from "react";
import "../../../styles/dashboardPagesStyle/Charts.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LabelList
} from "recharts";
import { Info } from "lucide-react";
import type { UnidadeCidade } from "../../../API/dashboardPageService";

interface PrecoMedioDataItem {
  cidade: string;
  Privativo: number;
}

interface Props {
  unidadesCidade: { [cidade: string]: UnidadeCidade } | null;
}

export default function PrecoMedioChart({ unidadesCidade }: Props) {
  const [data, setData] = useState<PrecoMedioDataItem[]>([]);
  const [popup, setPopup] = useState({ visible: false, text: "" });

  useEffect(() => {
    if (unidadesCidade) {
      const formatted = Object.entries(unidadesCidade)
        .map(([cidade, valores]) => ({
          cidade,
          Privativo: valores.preco_m2_medio,
        }))
        .sort((a, b) => b.Privativo - a.Privativo)
        .slice(0, 5);

      setData(formatted);
    }
  }, [unidadesCidade]);

  const handleInfoClick = () => {
    setPopup({
      visible: true,
      text: "Este gráfico mostra o preço médio do m² por cidade.",
    });
  };

  return (
    <div className="card-grafico-preco info-card">
      <h3>Preço médio do m²</h3>
      <Info className="info-btn" size={20} onClick={handleInfoClick} />

      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} margin={{ top: 20, right: 5, bottom: 5, left: 0 }}>
          <XAxis
            dataKey="cidade"
            interval={0}
            tick={({ x, y, payload }) => {
              const cidade = payload.value as string;

              // Capitaliza a primeira letra de cada palavra
              const words = cidade
                .toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1));

              // Divide em até 2 linhas
              const firstLine = words.slice(0, Math.ceil(words.length / 2)).join(' ');
              const secondLine = words.slice(Math.ceil(words.length / 2)).join(' ');

              // Ajuste vertical (y + offset) para ficar abaixo da linha do eixo
              const offsetY = 10;

              return (
                <g transform={`translate(${x},${y + offsetY})`}>
                  <text textAnchor="middle" fontSize={12} fill="#666">
                    <tspan x={0} dy={0}>{firstLine}</tspan>
                    {secondLine && <tspan x={0} dy={14}>{secondLine}</tspan>}
                  </text>
                </g>
              );
            }}
          />

          <YAxis style={{ fontSize: '12px' }} />
          <Tooltip formatter={(value: number) => `R$ ${value.toLocaleString('pt-BR')}`} />
          
          <Bar dataKey="Privativo" fill="#29306A">
            <LabelList
              dataKey="Privativo"
              position="top"
              content={({ x, y, width, value }: any) => {
                const xPos = x + width / 2; // centraliza horizontalmente
                const yPos = y - 6; // ajusta verticalmente acima da barra
                return (
                  <text
                    x={xPos}
                    y={yPos}
                    fill="#29306A"
                    fontSize={10}
                    textAnchor="middle"
                  >
                    {`R$ ${Number(value).toLocaleString('pt-BR')}`}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>


      {popup.visible && (
        <div className="info-popup-overlay" onClick={() => setPopup({ visible: false, text: "" })}>
          <div className="info-popup" onClick={e => e.stopPropagation()}>
            <h4>Informações</h4>
            <p>{popup.text}</p>
            <button onClick={() => setPopup({ visible: false, text: "" })}>Fechar</button>
          </div>
        </div>
      )}
    </div>
  );
}
